import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  useFinding,
  useOccurrences,
  useAckFinding,
  useSilenceFinding,
} from '@/lib/findings'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { SeverityBadge } from '@/components/SeverityBadge'

const DURATIONS = [
  { label: '1h', s: 3600 },
  { label: '4h', s: 4 * 3600 },
  { label: '24h', s: 24 * 3600 },
  { label: '7d', s: 7 * 24 * 3600 },
]

export function ErrorDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading } = useFinding(id)
  const { data: occ } = useOccurrences(id)
  const ack = useAckFinding()
  const sil = useSilenceFinding()

  async function doAck() {
    if (!id) return
    try {
      await ack.mutateAsync(id)
      toast.success('Acknowledged')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ACK failed')
    }
  }
  async function doSilence(s: number) {
    if (!id) return
    try {
      await sil.mutateAsync({ id, seconds: s })
      toast.success(`Silenced for ${s / 3600}h`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Silence failed')
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/errors" className="text-sm text-muted-foreground hover:underline">
          ← Back to errors
        </Link>
        <h1 className="text-2xl font-bold flex items-center gap-3">
          {data && <SeverityBadge severity={data.severity} />}
          <span className="truncate">{data?.signature_short ?? id}</span>
        </h1>
      </div>

      {isLoading && <Skeleton className="h-40" />}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Summary</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div>Pattern: <span className="text-muted-foreground">{data.pattern_matched}</span></div>
                <div>Count: <span className="text-muted-foreground">{data.count_total}</span></div>
                <div>First: <span className="text-muted-foreground">{new Date(data.first_seen).toLocaleString()}</span></div>
                <div>Last: <span className="text-muted-foreground">{new Date(data.last_seen).toLocaleString()}</span></div>
                <div>Stack: <span className="text-muted-foreground">{data.stack ?? '—'}</span></div>
                <div>Status: {data.acknowledged_at ? <span className="text-muted-foreground">acked</span> : data.silenced_until && new Date(data.silenced_until) > new Date() ? <span className="text-muted-foreground">silenced</span> : <span className="text-muted-foreground">active</span>}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Sample</CardTitle></CardHeader>
              <CardContent>
                <pre className="text-xs whitespace-pre-wrap text-muted-foreground">{data.sample_raw}</pre>
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader><CardTitle className="text-base">Actions</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button size="sm" onClick={doAck} disabled={ack.isPending}>Acknowledge</Button>
              {DURATIONS.map(d => (
                <Button key={d.s} size="sm" variant="outline" onClick={() => doSilence(d.s)} disabled={sil.isPending}>
                  Silence {d.label}
                </Button>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Recent occurrences</CardTitle></CardHeader>
            <CardContent className="divide-y">
              {occ?.slice(0, 30).map((o, i) => (
                <div key={i} className="py-2 text-xs">
                  <div className="text-muted-foreground">{new Date(o.ts).toLocaleString()}</div>
                  <pre className="whitespace-pre-wrap break-words">{o.raw_line}</pre>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
